import { createSlice } from '@reduxjs/toolkit'
import { type GeneratedFile } from 'common/files/files.types'
import { StoreStatus, type UserGroupDetails, type GroupUser } from 'common/utils/types'
import { editPlan } from 'common/requests/requests.service'
import { addUser } from 'common/users/users-page.service'

import {
  getMyGroupUsers,
  getGroupUsers,
  getMyStakeholderGroup,
  getStakeholderGroup,
} from './stakeholder.service'

export interface StakeholderGroupState {
  status: StoreStatus
  editUserStatus: StoreStatus
  addUserStatus: StoreStatus
  userGroup: UserGroupDetails | null
  groupUsers: GroupUser[]
  count: number
}

const initialState: StakeholderGroupState = {
  status: StoreStatus.Idle,
  editUserStatus: StoreStatus.Idle,
  addUserStatus: StoreStatus.Idle,
  userGroup: null,
  groupUsers: [],
  count: 0,
}

export const StakeholderSlice = createSlice({
  name: 'stakeholderProfile',
  initialState,
  reducers: {
    resetState: (state) => ({
      ...initialState,
    }),
    setStakeholderProfilePhoto: (state, { payload }: { payload: GeneratedFile }) => {
      return {
        ...state,
        userGroup: state.userGroup
          ? {
              ...state.userGroup,
              photo: payload.read?.url
                ? {
                    url: payload.read?.url,
                    expiresIn: payload.read?.expiresIn,
                  }
                : undefined,
            }
          : null,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addUser.fulfilled, (state, { payload }) => {
      state.groupUsers.push({
        ...payload,
        photo: undefined,
      })
    })
    builder.addCase(getStakeholderGroup.pending, (state, { payload }) => ({
      ...state,
      status: StoreStatus.Loading,
    }))
    builder.addCase(getStakeholderGroup.fulfilled, (state, { payload }) => {
      return {
        ...state,
        userGroup: payload,
        status: StoreStatus.Succeeded,
      }
    })
    builder.addCase(getStakeholderGroup.rejected, (state, { payload }) => {
      return {
        ...state,
        userGroup: null,
        status: StoreStatus.Failed,
      }
    })
    builder.addCase(getMyStakeholderGroup.pending, (state, { payload }) => ({
      ...state,
      status: StoreStatus.Loading,
    }))
    builder.addCase(getMyStakeholderGroup.fulfilled, (state, { payload }) => {
      return {
        ...state,
        userGroup: payload,
        status: StoreStatus.Succeeded,
      }
    })
    builder.addCase(getMyStakeholderGroup.rejected, (state, { payload }) => {
      return {
        ...state,
        userGroup: null,
        status: StoreStatus.Failed,
      }
    })
    builder.addCase(getMyGroupUsers.pending, (state, { payload }) => ({
      ...state,
      status: StoreStatus.Loading,
    }))
    builder.addCase(getMyGroupUsers.fulfilled, (state, { payload }) => {
      return {
        ...state,
        groupUsers: payload.groupUsers,
        count: payload.count,
        status: StoreStatus.Succeeded,
      }
    })
    builder.addCase(getMyGroupUsers.rejected, (state, { payload }) => {
      return {
        ...state,
        groupUsers: [],
        status: StoreStatus.Failed,
        count: 0,
      }
    })
    builder.addCase(getGroupUsers.pending, (state, { payload }) => ({
      ...state,
      status: StoreStatus.Loading,
    }))
    builder.addCase(getGroupUsers.fulfilled, (state, { payload }) => {
      return {
        ...state,
        groupUsers: payload.groupUsers,
        count: payload.count,
        status: StoreStatus.Succeeded,
      }
    })
    builder.addCase(getGroupUsers.rejected, (state, { payload }) => {
      return {
        ...state,
        groupUsers: [],
        status: StoreStatus.Failed,
        count: 0,
      }
    })
    builder.addCase(editPlan.fulfilled, (state, action) => {
      const { newSubscriptionLimit, newRequestsLimit, endDate } = action.meta.arg

      if (state.userGroup?.plan) {
        state.userGroup.plan = {
          ...state.userGroup.plan,
          period: {
            from: new Date(),
            to: new Date(endDate),
          },
          tracking: {
            ...state.userGroup.plan.tracking,
            max: newSubscriptionLimit,
          },
          requests: {
            ...state.userGroup.plan.requests,
            max: newRequestsLimit,
          },
        }
      }
    })
  },
})

export const { resetState, setStakeholderProfilePhoto } = StakeholderSlice.actions

export default StakeholderSlice.reducer
